import { Static, Type } from '@sinclair/typebox';
import {
  AdminCrmSubjectSchema,
  AdminCrmTaskItemSchema,
  CrmContactStatus,
  CrmLifecycleStage,
  CRM_CONSENT_VERBATIM_MAX_LENGTH,
} from './crm-contact';

import { AdminCrmTrainingSummarySchema } from './crm-training';
import {
  NON_CONVERSION_NOTE_MAX_LENGTH,
  NON_CONVERSION_REASONS_NOTE_REQUIRED,
  NonConversionReason,
} from './non-conversion';

// =====================================================================
// CRM — file d'appels, journal d'appels, notes (lot 1).
// cf. docs/to_implement/crm-suivi-apres-essai-plan.md. Enums dual-defined avec schema.prisma.
//
// Pas de créneaux ni de rotation (décision du 02/10/2026) : la file liste les appels du jour, la
// CSM appelle quand elle veut dans les heures de travail. Trois tentatives au plus, sur trois jours
// ouvrés différents : après une tentative, la suivante s'ouvre le lendemain (jamais deux le même
// jour, décision du 09/10/2026 ; plus de délai de 24 h). Une heure précise n'existe que si la
// personne a demandé à être rappelée : ce rappel passe en tête et gèle toute autre tentative.
// =====================================================================

export enum CrmQueueEntryReason {
  TRIAL_ENDED = 'TRIAL_ENDED',
  LEAD_NOT_SIGNED_UP = 'LEAD_NOT_SIGNED_UP',
  /** Lead qui a demandé à parler à quelqu'un pendant une conversation Pulse : appel prioritaire. */
  LEAD_WANTS_HUMAN = 'LEAD_WANTS_HUMAN',
  /** Client qui résilie. Réservé : non alimenté pour l'instant. */
  CHURN = 'CHURN',
  /** Rappel convenu avec la personne (ou décidé par l'équipe), hors cadence : passage « à la demande ». */
  FOLLOW_UP = 'FOLLOW_UP',
}

/**
 * Nature d'un passage en file.
 *  - RELANCE : la cadence du système (essai terminé, lead sans réponse, demande d'humain,
 *    résiliation) : trois tentatives, trois reports, règles de `call-rules.ts`.
 *  - ON_DEMAND : un appel décidé par un humain ou demandé par la personne, hors cadence : il naît
 *    toujours avec un rappel programmé, se reporte sans plafond, et ne déclenche pas de SMS de
 *    dernière chance. Un dossier n'a jamais qu'un passage ouvert, quelle que soit sa nature.
 */
export enum CrmQueueEntryKind {
  RELANCE = 'RELANCE',
  ON_DEMAND = 'ON_DEMAND',
}

/** Nature d'une ligne du journal d'appels : la nature du passage, ou « hors file » sans passage. */
export enum CrmCallJournalKind {
  RELANCE = 'RELANCE',
  ON_DEMAND = 'ON_DEMAND',
  FREE = 'FREE',
}

export enum CrmQueueEntryStatus {
  TO_CALL = 'TO_CALL',
  CALLBACK_SCHEDULED = 'CALLBACK_SCHEDULED',
  DONE = 'DONE',
  UNREACHABLE = 'UNREACHABLE',
  CLOSED = 'CLOSED',
}

export enum CrmQueueExitReason {
  HANDLED = 'HANDLED',
  SUBSCRIBED = 'SUBSCRIBED',
  SIGNED_UP = 'SIGNED_UP',
  UNREACHABLE = 'UNREACHABLE',
  INVALID_NUMBER = 'INVALID_NUMBER',
  CONTACT_REFUSED = 'CONTACT_REFUSED',
  REPEATED_POSTPONE = 'REPEATED_POSTPONE',
  MANUAL = 'MANUAL',
}

export enum CrmCallOutcome {
  REACHED = 'REACHED',
  CALLBACK = 'CALLBACK',
  NO_ANSWER = 'NO_ANSWER',
  INVALID_NUMBER = 'INVALID_NUMBER',
  DO_NOT_CALL = 'DO_NOT_CALL',
}

export enum CrmBlockedStep {
  /** La personne n'a bloqué sur aucune étape. */
  NONE = 'NONE',
  CATALOG = 'CATALOG',
  PRICING = 'PRICING',
  AGENDA = 'AGENDA',
  WEBSITE = 'WEBSITE',
  ONLINE_PAYMENT = 'ONLINE_PAYMENT',
  DOMAIN = 'DOMAIN',
  CUSTOMER_IMPORT = 'CUSTOMER_IMPORT',
  OTHER = 'OTHER',
}

export enum CrmTrainingOutcome {
  /** Inscrit à la main dans le compte Propelsia qui porte les formations. */
  ENROLLED = 'ENROLLED',
  REFUSED = 'REFUSED',
}

export enum CrmReasonSource {
  CSM_CALL = 'CSM_CALL',
  PUBLIC_PAGE = 'PUBLIC_PAGE',
  SYSTEM = 'SYSTEM',
}

/** Pourquoi un appel n'est pas possible maintenant. */
export enum CrmCallBlockReason {
  OUTSIDE_HOURS = 'OUTSIDE_HOURS',
  NOT_A_WORKING_DAY = 'NOT_A_WORKING_DAY',
  /** Une tentative a déjà eu lieu ce jour-là (chez la personne) : la suivante s'ouvre le lendemain. */
  TOO_SOON = 'TOO_SOON',
  /** Un rappel est programmé : c'est le seul appel possible, et pas avant son heure. */
  CALLBACK_PENDING = 'CALLBACK_PENDING',
  CONTACT_REFUSED = 'CONTACT_REFUSED',
  NO_PHONE = 'NO_PHONE',
  ENTRY_CLOSED = 'ENTRY_CLOSED',
}

/** Pourquoi une ligne est dans la section « En retard ». */
export enum CrmLateReason {
  CALLBACK_OVERDUE = 'CALLBACK_OVERDUE',
  NO_ATTEMPT_7_DAYS = 'NO_ATTEMPT_7_DAYS',
  TASK_OVERDUE = 'TASK_OVERDUE',
}

export const CRM_MAX_ATTEMPTS = 3;
export const CRM_MAX_POSTPONES = 3;
/** Heures de travail pour appeler, à l'heure de la personne. */
export const CRM_CALL_HOURS = { start_hour: 9, end_hour: 19 } as const;
/** Un passage en file depuis plus longtemps sans aucune tentative passe « en retard ». */
export const CRM_NO_ATTEMPT_LATE_DAYS = 7;
/** Les dossiers dont le fait générateur a moins de N jours passent devant le stock. */
export const CRM_FRESH_ENTRY_DAYS = 30;
/** Fenêtre de correction d'une note par son auteur. */
export const CRM_NOTE_EDIT_WINDOW_MINUTES = 10;
export const CRM_NOTE_MAX_LENGTH = 5000;

export const CRM_OPEN_QUEUE_ENTRY_STATUSES: readonly CrmQueueEntryStatus[] = [
  CrmQueueEntryStatus.TO_CALL,
  CrmQueueEntryStatus.CALLBACK_SCHEDULED,
];

// ── Saisie d'un appel : contrôles partagés par l'écran et le serveur ─────────────

export interface CrmCallInputLike {
  outcome: CrmCallOutcome | `${CrmCallOutcome}`;
  converted_during_call?: boolean;
  callback_at?: string | null;
  callback_verbatim?: string | null;
  non_conversion_reason?: NonConversionReason | `${NonConversionReason}` | null;
  blocked_step?: CrmBlockedStep | `${CrmBlockedStep}` | null;
  non_conversion_note?: string | null;
  training_outcome?: CrmTrainingOutcome | `${CrmTrainingOutcome}` | null;
  /** « Prévoir un nouvel appel » coché (écran) : la date du rappel devient obligatoire. */
  schedule_follow_up?: boolean;
  follow_up_at?: string | null;
}

/** Ce que l'écran et le serveur savent du passage pour contrôler la saisie. */
export interface CrmCallInputContext {
  /** Nature du passage ; `RELANCE` par défaut. Un appel hors file n'a pas de passage : `undefined`. */
  kind?: CrmQueueEntryKind | `${CrmQueueEntryKind}`;
}

export enum CrmCallInputError {
  CALLBACK_AT_REQUIRED = 'CALLBACK_AT_REQUIRED',
  CALLBACK_VERBATIM_REQUIRED = 'CALLBACK_VERBATIM_REQUIRED',
  REASON_REQUIRED = 'REASON_REQUIRED',
  BLOCKED_STEP_REQUIRED = 'BLOCKED_STEP_REQUIRED',
  BLOCKED_STEP_MUST_BE_SET = 'BLOCKED_STEP_MUST_BE_SET',
  NOTE_REQUIRED = 'NOTE_REQUIRED',
  TRAINING_OUTCOME_REQUIRED = 'TRAINING_OUTCOME_REQUIRED',
  FOLLOW_UP_AT_REQUIRED = 'FOLLOW_UP_AT_REQUIRED',
}

const hasText = (value: string | null | undefined): boolean => typeof value === 'string' && value.trim() !== '';

/**
 * Ce qui manque pour enregistrer un appel. Seul « joint » exige la raison, l'étape de blocage et
 * la formation (sauf si la personne s'abonne pendant l'appel) ; « à rappeler » exige la date et la
 * phrase de la personne. La note est obligatoire quand la raison ou l'étape vaut « autre », et
 * pour « il manque une fonctionnalité » (la fonctionnalité se lit dans la note).
 *
 * Sur un passage « à la demande », la raison, l'étape et la formation restent facultatives : le
 * cycle de relance les a déjà recueillies (ou n'a jamais existé). « Prévoir un nouvel appel »
 * exige sa date.
 */
export function validateCrmCallInput(input: CrmCallInputLike, context: CrmCallInputContext = {}): CrmCallInputError[] {
  const errors: CrmCallInputError[] = [];
  if (input.outcome === CrmCallOutcome.CALLBACK) {
    if (!hasText(input.callback_at)) errors.push(CrmCallInputError.CALLBACK_AT_REQUIRED);
    if (!hasText(input.callback_verbatim)) errors.push(CrmCallInputError.CALLBACK_VERBATIM_REQUIRED);
  }
  const reasonGiven = input.non_conversion_reason != null;
  const onDemand = context.kind === CrmQueueEntryKind.ON_DEMAND;
  if (input.outcome === CrmCallOutcome.REACHED && input.converted_during_call !== true && !onDemand) {
    if (!reasonGiven) errors.push(CrmCallInputError.REASON_REQUIRED);
    if (input.blocked_step == null) errors.push(CrmCallInputError.BLOCKED_STEP_REQUIRED);
    if (input.training_outcome == null) errors.push(CrmCallInputError.TRAINING_OUTCOME_REQUIRED);
  }
  if (input.outcome === CrmCallOutcome.REACHED && input.schedule_follow_up === true && !hasText(input.follow_up_at)) {
    errors.push(CrmCallInputError.FOLLOW_UP_AT_REQUIRED);
  }
  if (reasonGiven) {
    if (
      input.non_conversion_reason === NonConversionReason.BLOCKED_ON_STEP &&
      (input.blocked_step == null || input.blocked_step === CrmBlockedStep.NONE)
    ) {
      errors.push(CrmCallInputError.BLOCKED_STEP_MUST_BE_SET);
    }
    const noteRequired =
      NON_CONVERSION_REASONS_NOTE_REQUIRED.includes(input.non_conversion_reason as NonConversionReason) ||
      input.blocked_step === CrmBlockedStep.OTHER;
    if (noteRequired && !hasText(input.non_conversion_note)) errors.push(CrmCallInputError.NOTE_REQUIRED);
  }
  return errors;
}

// ── Schémas d'API admin ──────────────────────────────────────────────────────────

export const AdminCrmLifecycleSchema = Type.Object({
  stage: Type.Enum(CrmLifecycleStage),
  lead_since_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'lead_since' } })),
  signed_up_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'signed_up_at' } })),
  trial_end_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'trial_end_at' } })),
  client_since_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'client_since' } })),
  churned_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'churned_at' } })),
});
export type AdminCrmLifecycle = Static<typeof AdminCrmLifecycleSchema>;

/**
 * Appel relevé chez OVH (ligne de l'admin connecté ou d'un collègue) qui n'a pas encore sa ligne
 * dans le journal : la CSM a appelé mais n'a pas saisi l'issue.
 */
export const AdminCrmUnloggedCallSchema = Type.Object({
  crm_telephony_call_id: Type.Number(),
  /** ISO complet : repris tel quel comme début de l'appel quand la CSM le saisit. */
  started_at: Type.String(),
  started_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'started_at', with_time: true } }),
  duration_s: Type.Number(),
});
export type AdminCrmUnloggedCall = Static<typeof AdminCrmUnloggedCallSchema>;

/** Un passage en file, tel qu'affiché dans « Ma journée » et sur la fiche. */
export const AdminCrmQueueEntrySchema = Type.Object({
  crm_queue_entry_id: Type.Number(),
  crm_dossier_id: Type.Number(),
  subject: AdminCrmSubjectSchema,
  phone_e164: Type.Optional(Type.String()),
  entry_reason: Type.Enum(CrmQueueEntryReason),
  /** Relance (cadence du système) ou rappel à la demande. */
  kind: Type.Enum(CrmQueueEntryKind),
  status: Type.Enum(CrmQueueEntryStatus),
  lifecycle_stage: Type.Enum(CrmLifecycleStage),
  attempts_count: Type.Number(),
  postpone_count: Type.Number(),
  entered_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'entered_at' } }),
  reference_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'reference_at' } })),
  last_attempt_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_attempt_at', with_time: true } }),
  ),
  /** Rappel demandé, affiché à l'heure de l'équipe. */
  next_call_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'next_call_at', with_time: true } }),
  ),
  callback_verbatim: Type.Optional(Type.String()),
  /** Heure qu'il est chez la personne, renseignée quand son fuseau diffère de celui de l'équipe. */
  local_time_label: Type.Optional(Type.String()),
  can_call_now: Type.Boolean(),
  call_block_reason: Type.Optional(Type.Enum(CrmCallBlockReason)),
  next_allowed_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'next_allowed_at', with_time: true } }),
  ),
  late_reason: Type.Optional(Type.Enum(CrmLateReason)),
  pinned_note: Type.Optional(Type.String()),
  /** Score de configuration du compte (dernier relevé de l'essai), absent pour un lead sans compte. */
  config_score: Type.Optional(Type.Number()),
  non_conversion_reason: Type.Optional(Type.Enum(NonConversionReason)),
  blocked_step: Type.Optional(Type.Enum(CrmBlockedStep)),
  non_conversion_note: Type.Optional(Type.String()),
  reason_source: Type.Optional(Type.Enum(CrmReasonSource)),
  training_outcome: Type.Optional(Type.Enum(CrmTrainingOutcome)),
  training_refusal_note: Type.Optional(Type.String()),
  exit_reason: Type.Optional(Type.Enum(CrmQueueExitReason)),
  exited_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'exited_at' } })),
  last_chance_sms_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_chance_sms_at', with_time: true } }),
  ),
  /** Dernier appel relevé chez OVH pas encore saisi (passage ouvert seulement). */
  unlogged_call: Type.Optional(AdminCrmUnloggedCallSchema),
});
export type AdminCrmQueueEntry = Static<typeof AdminCrmQueueEntrySchema>;

export const AdminCrmWorkQueueCountsSchema = Type.Object({
  late: Type.Number(),
  callbacks_today: Type.Number(),
  priority: Type.Number(),
  queue: Type.Number(),
  /** Dossiers ouverts qu'on ne peut pas appeler aujourd'hui (déjà tentés aujourd'hui, rappel à venir). */
  waiting: Type.Number(),
  tasks: Type.Number(),
  /** Séances de formation passées depuis plus de 24 h dont des présences restent à saisir. */
  trainings_to_mark: Type.Optional(Type.Number()),
});

export const AdminCrmWorkQueueResponseSchema = Type.Object({
  counts: AdminCrmWorkQueueCountsSchema,
  /** Rappels dépassés et passages sans tentative depuis plus de sept jours. */
  late: Type.Array(AdminCrmQueueEntrySchema),
  /** Rappels programmés aujourd'hui, échus puis à venir. */
  callbacks_today: Type.Array(AdminCrmQueueEntrySchema),
  /** Leads qui ont demandé un humain. */
  priority: Type.Array(AdminCrmQueueEntrySchema),
  queue: Type.Array(AdminCrmQueueEntrySchema),
  waiting: Type.Array(AdminCrmQueueEntrySchema),
  /** Tâches ouvertes (échues et à venir). */
  tasks: Type.Array(AdminCrmTaskItemSchema),
  /** Plus de passages ouverts que l'écran n'en lit : les moins urgents (stock, rappels futurs) manquent. */
  truncated: Type.Optional(Type.Boolean()),
});
export type AdminCrmWorkQueueResponse = Static<typeof AdminCrmWorkQueueResponseSchema>;

export const AdminCrmCallItemSchema = Type.Object({
  crm_call_id: Type.Number(),
  /** Absent : appel « hors file » (support, rappel hors file), sans effet sur la file. */
  crm_queue_entry_id: Type.Optional(Type.Number()),
  /** Nature de l'appel : relance, rappel à la demande, ou hors file. */
  kind: Type.Enum(CrmCallJournalKind),
  subject: Type.Optional(AdminCrmSubjectSchema),
  called_by_admin_user_id: Type.Number(),
  called_by_name: Type.String(),
  called_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'called_at', with_time: true } }),
  outcome: Type.Enum(CrmCallOutcome),
  duration_s: Type.Optional(Type.Number()),
  /** La durée vient du détail des appels OVH (sinon : estimée par la fiche d'appel). */
  duration_measured: Type.Optional(Type.Boolean()),
  callback_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'callback_at', with_time: true } }),
  ),
  converted_during_call: Type.Boolean(),
  was_scheduled_callback: Type.Boolean(),
  call_note: Type.Optional(Type.String()),
});
export type AdminCrmCallItem = Static<typeof AdminCrmCallItemSchema>;

export const AdminCrmNoteItemSchema = Type.Object({
  crm_note_id: Type.Number(),
  author_admin_user_id: Type.Number(),
  author_name: Type.String(),
  body: Type.String(),
  pinned: Type.Boolean(),
  /** L'admin connecté peut encore corriger le texte (auteur, dans la fenêtre de dix minutes). */
  can_edit: Type.Boolean(),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  edited: Type.Boolean(),
});
export type AdminCrmNoteItem = Static<typeof AdminCrmNoteItemSchema>;

/** Tout le suivi humain d'une personne : cycle de vie, passage ouvert, historique, appels, notes. */
export const AdminCrmDossierSchema = Type.Object({
  crm_dossier_id: Type.Optional(Type.Number()),
  subject: AdminCrmSubjectSchema,
  phone_e164: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
  contact_status: Type.Enum(CrmContactStatus),
  lifecycle: AdminCrmLifecycleSchema,
  config_score: Type.Optional(Type.Number()),
  phone_invalid_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'phone_invalid_at' } }),
  ),
  /** Formation : séances visées, présences (absent quand aucun compte de formation n'est configuré). */
  training: Type.Optional(AdminCrmTrainingSummarySchema),
  open_entry: Type.Optional(AdminCrmQueueEntrySchema),
  past_entries: Type.Array(AdminCrmQueueEntrySchema),
  calls: Type.Array(AdminCrmCallItemSchema),
  notes: Type.Array(AdminCrmNoteItemSchema),
});
export type AdminCrmDossier = Static<typeof AdminCrmDossierSchema>;

export const AdminCrmCallLogBodySchema = Type.Object({
  /** Instant où le panneau d'appel a été ouvert (ISO complet) : la durée en est déduite. */
  started_at: Type.String(),
  outcome: Type.Enum(CrmCallOutcome),
  /** Rappel demandé : ISO complet (avec offset ou Z). */
  callback_at: Type.Optional(Type.String()),
  callback_verbatim: Type.Optional(Type.String({ maxLength: CRM_CONSENT_VERBATIM_MAX_LENGTH })),
  non_conversion_reason: Type.Optional(Type.Enum(NonConversionReason)),
  blocked_step: Type.Optional(Type.Enum(CrmBlockedStep)),
  non_conversion_note: Type.Optional(Type.String({ maxLength: NON_CONVERSION_NOTE_MAX_LENGTH })),
  training_outcome: Type.Optional(Type.Enum(CrmTrainingOutcome)),
  training_refusal_note: Type.Optional(Type.String({ maxLength: NON_CONVERSION_NOTE_MAX_LENGTH })),
  converted_during_call: Type.Optional(Type.Boolean()),
  call_note: Type.Optional(Type.String({ maxLength: CRM_NOTE_MAX_LENGTH })),
  /** « Ne veut plus être appelé » : la personne refuse aussi les emails et les SMS. */
  refuse_all_contact: Type.Optional(Type.Boolean()),
  /** Durée de la conversation suivie en direct sur la ligne OVH (décroché → raccroché), en secondes. */
  measured_duration_s: Type.Optional(Type.Integer({ minimum: 0, maximum: 4 * 60 * 60 })),
  /** Saisie d'un appel relevé chez OVH (`unlogged_call`) : il est rattaché à cette ligne du journal. */
  crm_telephony_call_id: Type.Optional(Type.Number()),
  /**
   * « Joint » + « prévoir un nouvel appel le … » : ce passage se ferme (traité) et un passage « à
   * la demande » s'ouvre avec ce rappel (ISO complet). Ignoré pour une autre issue.
   */
  follow_up_at: Type.Optional(Type.String()),
  follow_up_verbatim: Type.Optional(Type.String({ maxLength: CRM_CONSENT_VERBATIM_MAX_LENGTH })),
});
export type AdminCrmCallLogBody = Static<typeof AdminCrmCallLogBodySchema>;

/**
 * Appel « hors file » : support d'un client, rappel après une coupure, second appel du jour, lead
 * sans passage. Il va au journal sans compter comme tentative, même si la personne a un passage
 * ouvert ; pas de rappel programmé (il relève de la file), mais « ne veut plus être appelé »
 * s'enregistre au registre des refus.
 */
export const CRM_FREE_CALL_OUTCOMES: readonly CrmCallOutcome[] = [
  CrmCallOutcome.REACHED,
  CrmCallOutcome.NO_ANSWER,
  CrmCallOutcome.DO_NOT_CALL,
];

export const AdminCrmFreeCallLogBodySchema = Type.Object({
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
  /** ISO complet : début de l'appel. */
  started_at: Type.String(),
  outcome: Type.Enum(CrmCallOutcome),
  call_note: Type.Optional(Type.String({ maxLength: CRM_NOTE_MAX_LENGTH })),
  refuse_all_contact: Type.Optional(Type.Boolean()),
  measured_duration_s: Type.Optional(Type.Integer({ minimum: 0, maximum: 4 * 60 * 60 })),
  /**
   * Appel lancé depuis l'admin (`started_at` = lancement) : sans durée mesurée, elle s'estime
   * début → enregistrement. Absent : fiche ouverte après coup, pas de durée estimée.
   */
  dialed: Type.Optional(Type.Boolean()),
  /**
   * « Joint » + « prévoir un nouvel appel le … » : un passage « à la demande » s'ouvre avec ce
   * rappel (ISO complet). Refusé si la personne a déjà un passage ouvert. Ignoré pour une autre issue.
   */
  follow_up_at: Type.Optional(Type.String()),
  follow_up_verbatim: Type.Optional(Type.String({ maxLength: CRM_CONSENT_VERBATIM_MAX_LENGTH })),
});
export type AdminCrmFreeCallLogBody = Static<typeof AdminCrmFreeCallLogBodySchema>;

/** Téléphonie de l'admin connecté : une ligne OVH lui est attribuée (appel en un clic). */
export const AdminCrmTelephonyStatusSchema = Type.Object({
  click_to_call: Type.Boolean(),
});
export type AdminCrmTelephonyStatus = Static<typeof AdminCrmTelephonyStatusSchema>;

/** Appel en un clic d'un numéro affiché n'importe où dans l'admin. */
export const AdminTelephonyDialBodySchema = Type.Object({
  phone_e164: Type.String({ maxLength: 32 }),
});
export type AdminTelephonyDialBody = Static<typeof AdminTelephonyDialBodySchema>;

/** Appel lancé par OVH : le téléphone de la CSM sonne, puis OVH compose le numéro de la personne. */
export const AdminCrmDialResponseSchema = Type.Object({
  /** ISO complet : début de l'appel, repris par la fiche d'appel. */
  started_at: Type.String(),
  /**
   * Appel d'un numéro hors file : la personne a un passage ouvert qu'on peut appeler maintenant.
   * La fiche d'appel s'ouvre alors pour saisir l'appel dans le journal.
   */
  crm_queue_entry: Type.Optional(AdminCrmQueueEntrySchema),
  /** Sinon, la personne qui porte ce numéro : l'appel se saisit « hors file ». Absent si inconnu. */
  crm_subject: Type.Optional(AdminCrmSubjectSchema),
});
export type AdminCrmDialResponse = Static<typeof AdminCrmDialResponseSchema>;

export const AdminCrmNoteCreateBodySchema = Type.Object({
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
  body: Type.String({ minLength: 1, maxLength: CRM_NOTE_MAX_LENGTH }),
  pinned: Type.Optional(Type.Boolean()),
});
export type AdminCrmNoteCreateBody = Static<typeof AdminCrmNoteCreateBodySchema>;

export const AdminCrmNoteUpdateBodySchema = Type.Object({
  body: Type.Optional(Type.String({ minLength: 1, maxLength: CRM_NOTE_MAX_LENGTH })),
  pinned: Type.Optional(Type.Boolean()),
});
export type AdminCrmNoteUpdateBody = Static<typeof AdminCrmNoteUpdateBodySchema>;

/**
 * Entrée manuelle d'une personne dans la file. Sans `kind` (ou `RELANCE`) : un cycle de relance,
 * motif déduit de ce qu'est la personne. `ON_DEMAND` : un rappel planifié (« Planifier un
 * rappel »), `next_call_at` obligatoire, refusé si un passage est déjà ouvert.
 */
export const AdminCrmQueueEnterBodySchema = Type.Object({
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
  kind: Type.Optional(Type.Enum(CrmQueueEntryKind)),
  /** Rappel planifié : ISO complet (avec offset ou Z). */
  next_call_at: Type.Optional(Type.String()),
  verbatim: Type.Optional(Type.String({ maxLength: CRM_CONSENT_VERBATIM_MAX_LENGTH })),
});
export type AdminCrmQueueEnterBody = Static<typeof AdminCrmQueueEnterBodySchema>;

export const AdminCrmQueueSyncBodySchema = Type.Object({
  /** `false` par défaut : la passe ne fait que compter ce qu'elle ferait entrer. */
  apply: Type.Optional(Type.Boolean()),
});
export type AdminCrmQueueSyncBody = Static<typeof AdminCrmQueueSyncBodySchema>;

export const AdminCrmQueueSyncResponseSchema = Type.Object({
  applied: Type.Boolean(),
  trial_ended: Type.Number(),
  leads_not_signed_up: Type.Number(),
  leads_wants_human: Type.Number(),
  auto_exited: Type.Number(),
  /** Aperçu des personnes concernées (borné). */
  preview: Type.Array(
    Type.Object({
      subject: AdminCrmSubjectSchema,
      entry_reason: Type.Enum(CrmQueueEntryReason),
      reference_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'reference_at' } })),
    }),
  ),
});
export type AdminCrmQueueSyncResponse = Static<typeof AdminCrmQueueSyncResponseSchema>;

/** Journal d'appels (vue « par personne » : qui a passé quels appels). */
export const AdminCrmCallJournalResponseSchema = Type.Object({
  items: Type.Array(AdminCrmCallItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
  callers: Type.Array(Type.Object({ admin_user_id: Type.Number(), name: Type.String(), calls: Type.Number() })),
  /** Appels par nature (relances, à la demande, hors file), dans le périmètre de l'appelant filtré. */
  kinds: Type.Array(Type.Object({ kind: Type.Enum(CrmCallJournalKind), calls: Type.Number() })),
});
export type AdminCrmCallJournalResponse = Static<typeof AdminCrmCallJournalResponseSchema>;

// ── Envois du CRM (lot 2) ─────────────────────────────────────────────────────────

export enum CrmMessageKind {
  /** Le lendemain de la troisième tentative sans réponse : une question, un lien, une seule fois. */
  LAST_CHANCE = 'LAST_CHANCE',
  /** La CSM a coché « pas venu » : invitation à se réinscrire à une prochaine séance. */
  TRAINING_MISSED = 'TRAINING_MISSED',
  /** Sept jours après une absence sans réinscription : la page à une question. */
  TRAINING_LAST_CALL = 'TRAINING_LAST_CALL',
  /** Deux jours après une séance suivie : récapitulatif et lien d'abonnement. */
  TRAINING_ATTENDED = 'TRAINING_ATTENDED',
  /** Email de suite après une démo suivie (emplacement réglé dans « Campagnes »). */
  DEMO_ATTENDED_EMAIL = 'DEMO_ATTENDED_EMAIL',
  /** Email de suite après une formation suivie (emplacement réglé dans « Campagnes »). */
  TRAINING_ATTENDED_EMAIL = 'TRAINING_ATTENDED_EMAIL',
}

/** Envois par SMS (le `CrmSmsService` ne connaît que ceux-là). */
export type CrmSmsMessageKind = Exclude<
  CrmMessageKind,
  CrmMessageKind.DEMO_ATTENDED_EMAIL | CrmMessageKind.TRAINING_ATTENDED_EMAIL
>;

export enum CrmMessageStatus {
  RESERVED = 'RESERVED',
  SENT = 'SENT',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

/** Question posée sur la page publique à une question (le lien du SMS porte le contexte). */
export enum CrmFeedbackQuestion {
  /** « Qu'est-ce qui vous a arrêté ? » : la liste des raisons de non-conversion. */
  NON_CONVERSION = 'NON_CONVERSION',
  /** « Qu'est-ce qui vous a empêché de venir ? » : même liste, formulation adaptée. */
  TRAINING_MISSED = 'TRAINING_MISSED',
}

/** Validité du lien signé de la page à une question. */
export const CRM_FEEDBACK_LINK_TTL_DAYS = 30;
